import React, { useState, useEffect } from 'react';
import { Button } from './ui';
import api from '../services/api';

export default function FilePreviewModal({ document, onClose, onDownload, onDelete }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const fetchPreviewUrl = async () => {
      try {
        // Get the preview URL with proper authorization
        const response = await api.get(`/documents/${document.id}/preview`, { responseType: 'blob' });
        const url = URL.createObjectURL(response.data);
        setPreviewUrl(url);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch preview:', err);
        setError('Failed to load preview');
        setLoading(false);
      }
    };

    fetchPreviewUrl();

    // Cleanup function to revoke object URL
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [document.id]);

  const isImage = document.mime_type?.startsWith('image/');
  const isPDF = document.mime_type === 'application/pdf';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{document.name}</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <div className="modal-body">
          {error ? (
            <div className="error-message">
              {error}
            </div>
          ) : loading ? (
            <div className="loading">Loading preview...</div>
          ) : previewUrl ? (
            <>
              {isImage && (
                <div className="image-preview">
                  <img 
                    src={previewUrl}
                    alt={document.name}
                    className="file-preview-media"
                  />
                </div>
              )}
              {isPDF && (
                <div className="pdf-preview">
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    width="100%"
                    height="500px"
                  >
                    <p>PDF preview is not available in your browser. Please download to view.</p>
                  </object>
                </div>
              )}
              {!isImage && !isPDF && (
                <div className="file-info">
                  <p>File type: {document.mime_type}</p>
                  <p>Preview not available for this file type.</p>
                  <p>Click download to view the file contents.</p>
                </div>
              )}
            </>
          ) : (
            <div className="error-message">
              Failed to generate preview. Please try downloading the file instead.
            </div>
          )}
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={onDownload}>
            Download
          </Button>
          <Button variant="danger" onClick={onDelete}>
            Delete
          </Button>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </div>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .modal-content {
            background: white;
            border-radius: 8px;
            width: 90%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
          }

          .modal-header {
            padding: 16px;
            border-bottom: 1px solid #eee;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .modal-header h3 {
            margin: 0;
            font-size: 1.25rem;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            padding: 0;
            color: #666;
          }

          .modal-body {
            padding: 16px;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          }

          .modal-footer {
            padding: 16px;
            border-top: 1px solid #eee;
            display: flex;
            justify-content: flex-end;
            gap: 8px;
          }

          .file-info {
            text-align: center;
            color: #666;
            padding: 20px;
          }

          .loading {
            text-align: center;
            color: #666;
            padding: 20px;
          }

          .error-message {
            color: #de350b;
            background-color: #ffebe6;
            padding: 16px;
            border-radius: 4px;
            text-align: center;
          }

          .image-preview {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
            background-color: #f4f5f7;
            border-radius: 4px;
            padding: 16px;
          }

          .file-preview-media {
            max-width: 100%;
            max-height: 80vh;
            object-fit: contain;
          }

          .pdf-preview {
            min-height: min(50vh, 500px);
            background-color: #f4f5f7;
            border-radius: 4px;
            padding: 16px;
          }
        `}</style>
      </div>
    </div>
  );
}